import { Head, Link, router } from "@inertiajs/react"
import {
  Check,
  ChevronDown,
  CirclePlus,
  ClipboardList,
  Plus,
  SquarePen,
  Trash2,
} from "lucide-react"
import { useState } from "react"

import { Breadcrumbs } from "@/components/breadcrumbs"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import AppLayout from "@/layouts/app-layout"
import {
  formatDate as formatDateUtil,
  formatUSDate,
  parseLocalDate,
} from "@/lib/date-utils"
import { formatPhone } from "@/lib/format-phone"
import { ensureUrl } from "@/lib/format-url"
import { objectPositionStyle } from "@/lib/utils"
import {
  detailCaseBillingReportsPath,
  selectFinalReportsCaseReportsPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

import { SubjectAddModals } from "./_subject-add-modals"
import type { SubjectModalType } from "./_subject-add-modals"

/* ── Types ──────────────────────────────────────────────── */

interface CaseSummary {
  id: number
  case_number: string
  display_name: string
}

interface SubjectAddress {
  id: number
  street: string | null
  street2: string | null
  subdivision: string | null
  city: string | null
  state: string | null
  zip: string | null
  dates: string | null
  full_address: string
  notes: string | null
}

interface SubjectAlias {
  id: number
  prefix: string | null
  first_name: string | null
  middle_name: string | null
  last_name: string | null
  suffix: string | null
  notes: string | null
  full_name: string
}

interface SubjectContact {
  id: number
  contact_type_id: number | null
  category: string | null
  modal_type: SubjectModalType
  info: string | null
  notes: string | null
}

interface SubjectVehicle {
  id: number
  description: string | null
  tag: string | null
  tag_state: string | null
  display_name: string
  notes: string | null
}

interface EmploymentRecord {
  id: number
  company_name: string | null
  position: string | null
  address: string | null
  city: string | null
  state: string | null
  zip: string | null
  phone: string | null
  start_date: string | null
  end_date: string | null
  schedule: string | null
  supervisor: string | null
  reason_for_leaving: string | null
  display_name: string
  full_address: string
  date_range: string | null
  verified: boolean
}

interface CriminalRecord {
  id: number
  offense_date: string | null
  county: string | null
  state: string | null
  conviction: string | null
  disposition: string | null
  notes: string | null
  verified: boolean
}

interface EducationRecord {
  id: number
  institution: string | null
  city: string | null
  state: string | null
  start_date: string | null
  end_date: string | null
  degree: string | null
  major: string | null
  verified: boolean
  date_range: string | null
}

interface SubjectPhoto {
  id: number
  filename: string | null
  caption: string | null
  display_name: string
  focal_point_x?: number | null
  focal_point_y?: number | null
  created_at: string
  image_url: string | null
  thumb_url: string | null
}

interface SubjectDetail {
  id: number
  subject_type: string | null
  type_description: string | null
  display_name: string
  full_name: string
  prefix: string | null
  first_name: string | null
  middle_name: string | null
  last_name: string | null
  suffix: string | null
  address: string | null
  address2: string | null
  subdivision: string | null
  city: string | null
  state: string | null
  zip: string | null
  address_dates: string | null
  home_phone: string | null
  cell_phone: string | null
  work_phone: string | null
  email: string | null
  sex: string | null
  race: string | null
  date_of_birth: string | null
  ssn_last4: string | null
  marital_status: string | null
  spouse_name: string | null
  divorce_date: string | null
  dependents: string | null
  description: string | null
  hobbies: string | null
  internal_notes: string | null
  drivers_license: string | null
  drivers_license_state: string | null
  drivers_license_name: string | null
  drivers_license_expires: string | null
  law_firm: string | null
  attorney: string | null
  place_of_employment: string | null
  poe_address: string | null
  poe_city: string | null
  poe_state: string | null
  poe_zip: string | null
  work_schedule: string | null
  ins_date_of_loss: string | null
  ins_injury: string | null
  ins_limits: string | null
  ins_contact: string | null
  ins_contact_phone: string | null
  ins_benefits: string | null
  ins_working: string | null
  ins_rehab: string | null
  ins_rehab_loc: string | null
  ins_may_we_contact: string | null
  ins_accident: string | null
  ins_appts: string | null
  ins_appt_info: string | null
  ins_prev_surveillance: string | null
  ins_prev_surveillance_date: string | null
  thumb_url: string | null
  focal_point_x?: number | null
  focal_point_y?: number | null
  photos_url: string
  addresses: SubjectAddress[]
  aliases: SubjectAlias[]
  contacts: SubjectContact[]
  vehicles: SubjectVehicle[]
  employment_records: EmploymentRecord[]
  criminal_records: CriminalRecord[]
  education_records: EducationRecord[]
  photos: SubjectPhoto[]
}

interface MenuCounts {
  tasks: number
  updates: number
  accounting_notes: number
  restricted_notes: number
  schedules: number
  equipment: number
  evidence: number
  expenses: number
  gps: number
  time_entries: number
  daily_reports: number
  gps_reports: number
  incident_reports: number
  documents: number
  photos: number
  contacts: number
}

interface AddPermissions {
  case_status: number
  can_add_case_notes: boolean
  can_update_contact: boolean
  can_add_schedule: boolean
  can_add_people: boolean
  can_prepare_reports: boolean
  can_add_restricted: boolean
  is_co: boolean
  can_view_final_report: boolean
  can_view_billing_report: boolean
}

interface ContactTypeOption {
  id: number
  name: string
}

interface ContactTypes {
  email: ContactTypeOption[]
  phone: ContactTypeOption[]
  social: ContactTypeOption[]
}

interface Props {
  case_record: CaseSummary
  subject: SubjectDetail
  menu_counts: MenuCounts
  add_permissions: AddPermissions
  can_add_sub_items: boolean
  can_edit_sub_items: boolean
  can_delete_sub_items: boolean
  contact_types: ContactTypes
}

/* ── Constants ─────────────────────────────────────────── */

const CASE_STATUS = {
  POTENTIAL: 0,
  ACTIVE: 1,
  CLOSED: 2,
  PENDING_REPORT: 3,
  PENDING_PAYMENT: 4,
} as const

/* ── Helpers ────────────────────────────────────────────── */

const GENDER_MAP: Record<string, string> = {
  M: "Male",
  F: "Female",
}

function genderLabel(sex: string | null): string {
  if (!sex) return ""
  return GENDER_MAP[sex] ?? sex
}

function formatDateOnly(date: string | null): string {
  return formatUSDate(date)
}

function formatDate(date: string | null): string {
  return formatDateUtil(date, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })
}

function formatMonthYear(date: string | null): string {
  if (!date) return ""
  const d = parseLocalDate(date)
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`
}

function googleMapsUrl(
  address: string | null,
  city: string | null,
  state: string | null,
  zip: string | null,
): string {
  const parts = [address, city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`
}

/* ── Verified Badge ─────────────────────────────────────── */

function VerifiedBadge({ verified }: { verified: boolean }) {
  if (!verified) return null
  return <Check className="text-hpi-dark-green ml-1 inline h-4 w-4" />
}

/* ── Section Component ──────────────────────────────────── */

function Section({
  id,
  title,
  children,
}: {
  id?: string
  title: string
  children: React.ReactNode
}) {
  return (
    <div id={id} className="border-hpi-light-gray mb-6 rounded border bg-white">
      <h3 className="border-hpi-light-gray bg-hpi-pale-gray text-hpi-dark-gray border-b px-4 py-2 text-sm font-semibold">
        {title}
      </h3>
      <div className="px-4 py-3 text-sm">{children}</div>
    </div>
  )
}

/* ── Photo Placeholder ──────────────────────────────────── */

function PhotoPlaceholder() {
  return (
    <div
      className="bg-hpi-light-gray flex h-[180px] w-[180px] items-center justify-center rounded"
      title="Photo Not Available"
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        className="text-hpi-med-gray"
      >
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle
          cx="8.5"
          cy="8.5"
          r="1.5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M6 17l3.5-4.5 2.5 3L15.5 11 19 17H6z"
          fill="currentColor"
          opacity="0.3"
        />
      </svg>
    </div>
  )
}

/* ── Top Bar Helpers ────────────────────────────────────── */

function TabLink({
  href,
  children,
  className = "",
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <a
      href={href}
      className={`text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1 text-sm transition-colors ${className}`}
    >
      {children}
    </a>
  )
}

function TabDropdown({
  label,
  items,
}: {
  label: string
  items: { label: string; href: string }[]
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1 text-sm transition-colors outline-none">
        <span>{label}</span>
        <ChevronDown className="h-3 w-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {items.map((item) => (
          <DropdownMenuItem key={item.href} asChild>
            <a href={item.href} className="cursor-pointer">
              {item.label}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function buildAddItems(
  caseId: number,
  p: AddPermissions,
): { label: string; href: string }[] {
  const s = p.case_status
  const items: { label: string; href: string }[] = []

  const canExpense =
    s === CASE_STATUS.ACTIVE ||
    (s >= CASE_STATUS.PENDING_REPORT && p.can_prepare_reports)

  if (p.is_co)
    items.push({
      label: "Accounting Note",
      href: `/cases/${caseId}/logs/new?type=accounting`,
    })
  if (p.can_add_case_notes)
    items.push({
      label: "Case Update",
      href: `/cases/${caseId}/logs/new?type=update`,
    })
  if (p.can_update_contact && s <= CASE_STATUS.ACTIVE)
    items.push({
      label: "Contact Info",
      href: `/cases/${caseId}/contacts/new`,
    })
  if (s <= CASE_STATUS.ACTIVE)
    items.push({
      label: "Documents",
      href: `/cases/${caseId}/documents/new`,
    })
  if (s !== CASE_STATUS.CLOSED)
    items.push({
      label: "Equipment/GPS",
      href: `/cases/${caseId}/equipment_assignments/new`,
    })
  if (canExpense) {
    items.push({ label: "Expense", href: `/cases/${caseId}/expenses/new` })
    items.push({
      label: "Per Diem",
      href: `/cases/${caseId}/expenses/new?per_diem=true`,
    })
    items.push({
      label: "Case Photos",
      href: `/cases/${caseId}/photos/new`,
    })
    items.push({ label: "Reports", href: `/cases/${caseId}/reports/new` })
  }
  if (p.can_add_restricted)
    items.push({
      label: "Restricted Note",
      href: `/cases/${caseId}/logs/new?type=restricted`,
    })
  if (p.can_add_schedule && s <= CASE_STATUS.ACTIVE)
    items.push({ label: "Schedule", href: `/cases/${caseId}/schedules/new` })
  if (p.can_add_people && s <= CASE_STATUS.ACTIVE)
    items.push({
      label: "Subject/People",
      href: `/cases/${caseId}/subjects/new`,
    })
  if (s !== CASE_STATUS.CLOSED)
    items.push({ label: "Task", href: `/cases/${caseId}/logs/new?type=todo` })
  if (canExpense)
    items.push({ label: "Time", href: `/cases/${caseId}/time_entries/new` })
  return items.sort((a, b) => a.label.localeCompare(b.label))
}

/* ── Subject Add dropdown items ─────────────────────────── */

const SUBJECT_ADD_ITEMS: { label: string; modal: SubjectModalType }[] = [
  { label: "Address", modal: "address" },
  { label: "Alias", modal: "alias" },
  { label: "Criminal History", modal: "criminal" },
  { label: "Education", modal: "education" },
  { label: "Email", modal: "contact_email" },
  { label: "Employment", modal: "employment" },
  { label: "Phone", modal: "contact_phone" },
  { label: "Social Media", modal: "contact_social" },
  { label: "Vehicle", modal: "vehicle" },
]

/* ── Main Component ─────────────────────────────────────── */

export default function Show({
  case_record,
  subject,
  menu_counts: mc,
  add_permissions: ap,
  can_add_sub_items,
  can_edit_sub_items,
  can_delete_sub_items,
  contact_types,
}: Props) {
  const c = case_record
  const s = subject
  const [activeModal, setActiveModal] = useState<SubjectModalType>(null)
  const [editingRecord, setEditingRecord] = useState<Record<
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  > | null>(null)

  const openEditModal = (
    modal: SubjectModalType,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    record: Record<string, any>,
  ) => {
    setEditingRecord(record)
    setActiveModal(modal)
  }

  const handleDeleteSubItem = (resourcePath: string, label: string) => {
    if (confirm(`Are you sure you want to delete this ${label}?`)) {
      router.delete(resourcePath)
    }
  }

  const closeModal = () => {
    setActiveModal(null)
    setEditingRecord(null)
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    { title: c.display_name || c.case_number, href: `/cases/${c.id}` },
    { title: s.display_name, href: `/cases/${c.id}/subjects/${s.id}` },
  ]

  /* -- Build case-level top bar dropdowns -- */
  const updatesItems: { label: string; href: string }[] = []
  if (mc.updates > 0)
    updatesItems.push({
      label: `Updates (${mc.updates})`,
      href: `/cases/${c.id}/logs?type=update`,
    })
  if (mc.accounting_notes > 0 && ap.is_co)
    updatesItems.push({
      label: `Accounting Notes (${mc.accounting_notes})`,
      href: `/cases/${c.id}/logs?type=accounting`,
    })
  if (mc.restricted_notes > 0 && ap.can_add_restricted)
    updatesItems.push({
      label: `Restricted Notes (${mc.restricted_notes})`,
      href: `/cases/${c.id}/logs?type=restricted`,
    })

  const activityItems: { label: string; href: string }[] = []
  if (mc.schedules > 0)
    activityItems.push({
      label: `Scheduled Work (${mc.schedules})`,
      href: `/cases/${c.id}/schedules`,
    })
  if (mc.equipment > 0)
    activityItems.push({
      label: `Equipment (${mc.equipment})`,
      href: `/cases/${c.id}/equipment_assignments`,
    })
  if (mc.evidence > 0)
    activityItems.push({
      label: `Evidence (${mc.evidence})`,
      href: `/cases/${c.id}/evidences`,
    })
  if (mc.expenses > 0)
    activityItems.push({
      label: `Expenses (${mc.expenses})`,
      href: `/cases/${c.id}/expenses`,
    })
  if (mc.gps > 0)
    activityItems.push({
      label: `GPS (${mc.gps})`,
      href: `/cases/${c.id}/gps`,
    })
  if (mc.time_entries > 0)
    activityItems.push({
      label: `Time (${mc.time_entries})`,
      href: `/cases/${c.id}/time_entries`,
    })
  const reportsItems: { label: string; href: string }[] = []
  if (mc.daily_reports > 0)
    reportsItems.push({
      label: `Daily Reports (${mc.daily_reports})`,
      href: `/cases/${c.id}/reports?category=daily`,
    })
  if (mc.gps_reports > 0)
    reportsItems.push({
      label: `GPS Reports (${mc.gps_reports})`,
      href: `/cases/${c.id}/reports?category=gps`,
    })
  if (mc.incident_reports > 0)
    reportsItems.push({
      label: `Incident Reports (${mc.incident_reports})`,
      href: `/cases/${c.id}/reports?category=incident`,
    })
  if (ap.can_view_final_report)
    reportsItems.push({
      label: "Final Report",
      href: selectFinalReportsCaseReportsPath(c.id),
    })
  if (ap.can_view_billing_report)
    reportsItems.push({
      label: "Billing Report",
      href: detailCaseBillingReportsPath(c.id),
    })

  const addItems = buildAddItems(c.id, ap)

  const caseTabsBar = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-6">
        <a
          href={`/cases/${c.id}/worksheet`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1 text-sm transition-colors"
        >
          <ClipboardList className="h-4 w-4" />
          <span>Worksheet</span>
        </a>

        {mc.tasks > 0 && (
          <a
            href={`/cases/${c.id}/logs?type=todo`}
            className="flex items-center gap-1 text-sm text-red-600 transition-colors hover:text-red-800"
          >
            Tasks ({mc.tasks})
          </a>
        )}

        {updatesItems.length > 0 && (
          <TabDropdown label="Updates" items={updatesItems} />
        )}

        {activityItems.length > 0 && (
          <TabDropdown label="Activity" items={activityItems} />
        )}

        {reportsItems.length > 0 && (
          <TabDropdown label="Reports" items={reportsItems} />
        )}

        {mc.documents > 0 && (
          <TabLink href={`/cases/${c.id}/documents`}>
            Documents ({mc.documents})
          </TabLink>
        )}

        {mc.photos > 0 && (
          <TabLink href={`/cases/${c.id}/photos`}>
            Case Photos ({mc.photos})
          </TabLink>
        )}

        {mc.contacts > 0 && (
          <TabLink href={`/cases/${c.id}/contacts`}>
            Contacts ({mc.contacts})
          </TabLink>
        )}
      </div>

      {addItems.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1.5 text-sm outline-none">
            <CirclePlus className="h-4 w-4" />
            <span>Add</span>
            <ChevronDown className="h-3 w-3" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {addItems.map((item) => (
              <DropdownMenuItem key={item.href} asChild>
                <a href={item.href} className="cursor-pointer">
                  {item.label}
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )

  const hasInsurance = !!(
    s.ins_date_of_loss ||
    s.ins_injury ||
    s.ins_limits ||
    s.ins_accident ||
    s.ins_contact ||
    s.ins_rehab_loc ||
    s.ins_appt_info ||
    s.ins_prev_surveillance_date
  )

  const jumpLinks: { label: string; anchor: string; show: boolean }[] = [
    { label: "Addresses", anchor: "addresses", show: s.addresses.length > 0 },
    {
      label: "Criminal History",
      anchor: "criminal",
      show: s.criminal_records.length > 0,
    },
    {
      label: "Education",
      anchor: "education",
      show: s.education_records.length > 0,
    },
    {
      label: "Employment",
      anchor: "employment",
      show: s.employment_records.length > 0,
    },
    { label: "Vehicles", anchor: "vehicles", show: s.vehicles.length > 0 },
    { label: "Photos", anchor: "photos", show: true },
    { label: "Insurance", anchor: "insurance", show: hasInsurance },
  ]
  const visibleJumps = jumpLinks.filter((j) => j.show)

  const currentAddress = [s.address, s.address2].filter(Boolean).join(", ")
  const currentCsz = [s.city, [s.state, s.zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
  const poeAddress = [s.poe_address].filter(Boolean).join(", ")
  const poeCsz = [
    s.poe_city,
    [s.poe_state, s.poe_zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ")

  return (
    <AppLayout topBar={caseTabsBar}>
      <Head title={`${s.display_name} - ${c.case_number}`} />

      <div className="p-6">
        {/* Breadcrumbs */}
        <div className="mb-4">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </div>

        {/* Subject header with Add button */}
        <div className="mb-6 flex items-center gap-3">
          <h1 className="text-hpi-dark-gray text-xl font-semibold">
            <span className="text-hpi-active-blue">
              {s.subject_type ?? "Subject"}:
            </span>{" "}
            {s.display_name}
          </h1>
          <Link
            href={`/cases/${c.id}/subjects/${s.id}/edit`}
            className="text-hpi-med-blue hover:text-hpi-active-blue"
          >
            <SquarePen className="h-4 w-4" />
          </Link>

          {/* Spacer to push Add button right */}
          <div className="flex-1" />

          {/* Subject-level Add dropdown */}
          {can_add_sub_items && (
            <DropdownMenu>
              <DropdownMenuTrigger className="bg-hpi-active-blue hover:bg-hpi-dark-blue flex items-center gap-1.5 rounded px-3 py-1.5 text-sm text-white transition-colors outline-none">
                <Plus className="h-4 w-4" />
                <span>Add</span>
                <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {SUBJECT_ADD_ITEMS.map((item) => (
                  <DropdownMenuItem
                    key={item.label}
                    onClick={() => setActiveModal(item.modal)}
                    className="cursor-pointer"
                  >
                    {item.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link href={s.photos_url} className="cursor-pointer">
                    Photo
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Two-column layout: info left, phone/contacts right */}
        <div className="mb-6 grid grid-cols-[1fr_340px] gap-6">
          {/* Left column */}
          <div className="text-hpi-dark-gray space-y-3 text-sm">
            {/* Photo + address block */}
            <div className="flex gap-4">
              {/* Thumbnail */}
              <div className="shrink-0">
                {s.thumb_url ? (
                  <img
                    src={s.thumb_url}
                    alt={s.display_name}
                    className="h-[180px] w-[180px] rounded object-cover"
                    style={{ objectPosition: objectPositionStyle(s) }}
                  />
                ) : (
                  <PhotoPlaceholder />
                )}
              </div>

              <div className="space-y-2">
                {/* Address */}
                {(currentAddress || currentCsz) && (
                  <div>
                    <div className="font-semibold">Address:</div>
                    <a
                      href={googleMapsUrl(s.address, s.city, s.state, s.zip)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-hpi-active-blue"
                    >
                      {s.address && <div>{s.address}</div>}
                      {s.address2 && <div>{s.address2}</div>}
                      {currentCsz && <div>{currentCsz}</div>}
                    </a>
                    {s.subdivision && <div>Subdivision: {s.subdivision}</div>}
                    {s.address_dates && <div>Dates: {s.address_dates}</div>}
                  </div>
                )}

                {/* SSN */}
                {s.ssn_last4 && (
                  <div>
                    <span className="font-semibold">SSN:</span> xxx-xx-
                    {s.ssn_last4}
                  </div>
                )}

                {/* DOB */}
                {s.date_of_birth && (
                  <div>
                    <span className="font-semibold">DoB:</span>{" "}
                    {formatDateOnly(s.date_of_birth)}
                  </div>
                )}

                {/* Race / Gender */}
                {s.race && (
                  <div>
                    <span className="font-semibold">Ethnicity:</span> {s.race}
                  </div>
                )}
                {genderLabel(s.sex) && (
                  <div>
                    <span className="font-semibold">Gender:</span>{" "}
                    {genderLabel(s.sex)}
                  </div>
                )}

                {/* Marital */}
                {s.marital_status && (
                  <div>
                    <span className="font-semibold">Marital:</span>{" "}
                    {s.marital_status}
                    {s.spouse_name && (
                      <span className="ml-3">
                        <span className="font-semibold">Spouse:</span>{" "}
                        {s.spouse_name}
                      </span>
                    )}
                    {s.divorce_date && (
                      <div>
                        <span className="font-semibold">Divorced:</span>{" "}
                        {s.divorce_date}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column: phones, contacts, aliases, DL, employer */}
          <div className="text-hpi-dark-gray space-y-3 text-sm">
            {s.home_phone && (
              <div>
                <span className="font-semibold">Home:</span>{" "}
                {formatPhone(s.home_phone)}
              </div>
            )}
            {s.cell_phone && (
              <div>
                <span className="font-semibold">Cell:</span>{" "}
                {formatPhone(s.cell_phone)}
              </div>
            )}
            {s.work_phone && (
              <div>
                <span className="font-semibold">Work:</span>{" "}
                {formatPhone(s.work_phone)}
              </div>
            )}
            {s.email && (
              <div>
                <span className="font-semibold">Email:</span> {s.email}
              </div>
            )}

            {/* Additional contacts */}
            {s.contacts.length > 0 && (
              <div className="border-hpi-light-gray mt-3 border-t border-dotted pt-2">
                <div className="mb-1 font-semibold">Additional Contacts</div>
                {s.contacts.map((ct) => (
                  <div key={ct.id} className="mt-0.5 flex items-start gap-1">
                    <div className="flex-1">
                      <span className="text-hpi-med-blue">{ct.category}:</span>{" "}
                      {ensureUrl(ct.info) ? (
                        <a
                          href={ensureUrl(ct.info)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-hpi-active-blue hover:underline"
                        >
                          {ct.info}
                        </a>
                      ) : ct.info &&
                        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ct.info) ? (
                        <a
                          href={`mailto:${ct.info}`}
                          className="text-hpi-active-blue hover:underline"
                        >
                          {ct.info}
                        </a>
                      ) : ct.modal_type === "contact_phone" ? (
                        formatPhone(ct.info)
                      ) : (
                        ct.info
                      )}
                      {ct.notes && (
                        <div className="text-hpi-med-gray ml-4 text-xs">
                          {ct.notes}
                        </div>
                      )}
                    </div>
                    {can_edit_sub_items && (
                      <button
                        onClick={() => openEditModal(ct.modal_type, ct)}
                        className="text-hpi-med-blue hover:text-hpi-active-blue mt-0.5 shrink-0 cursor-pointer"
                      >
                        <SquarePen className="h-3 w-3" />
                      </button>
                    )}
                    {can_delete_sub_items && (
                      <button
                        onClick={() =>
                          handleDeleteSubItem(
                            `/cases/${c.id}/subjects/${s.id}/contacts/${ct.id}`,
                            "contact",
                          )
                        }
                        className="text-hpi-med-gray hover:text-hpi-red mt-0.5 shrink-0 cursor-pointer"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Aliases */}
            {s.aliases.length > 0 && (
              <div className="border-hpi-light-gray mt-3 border-t border-dotted pt-2">
                <div className="mb-1 font-semibold">Aliases</div>
                {s.aliases.map((a) => (
                  <div key={a.id} className="flex items-center gap-1">
                    <span className="flex-1">{a.full_name}</span>
                    {can_edit_sub_items && (
                      <button
                        onClick={() => openEditModal("alias", a)}
                        className="text-hpi-med-blue hover:text-hpi-active-blue shrink-0 cursor-pointer"
                      >
                        <SquarePen className="h-3 w-3" />
                      </button>
                    )}
                    {can_delete_sub_items && (
                      <button
                        onClick={() =>
                          handleDeleteSubItem(
                            `/cases/${c.id}/subjects/${s.id}/aliases/${a.id}`,
                            "alias",
                          )
                        }
                        className="text-hpi-med-gray hover:text-hpi-red shrink-0 cursor-pointer"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Driver's License */}
            {s.drivers_license && (
              <div className="border-hpi-light-gray mt-3 border-t border-dotted pt-2">
                <div>
                  <span className="font-semibold">Drivers License:</span>{" "}
                  {s.drivers_license_state} #{s.drivers_license}
                </div>
                {s.drivers_license_name && (
                  <div>
                    <span className="font-semibold">Name on License:</span>{" "}
                    {s.drivers_license_name}
                  </div>
                )}
                {s.drivers_license_expires && (
                  <div>Expires: {s.drivers_license_expires}</div>
                )}
              </div>
            )}

            {/* Current employer */}
            {s.place_of_employment && (
              <div className="border-hpi-light-gray mt-3 border-t border-dotted pt-2">
                <div className="font-semibold">Current Employer:</div>
                <div>{s.place_of_employment}</div>
                {(poeAddress || poeCsz) && (
                  <div>
                    {poeAddress && <span>{poeAddress}, </span>}
                    {poeCsz && <span>{poeCsz}</span>}
                  </div>
                )}
                {s.work_schedule && (
                  <div className="text-hpi-med-blue mt-1 text-xs">
                    {s.work_schedule}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Jump links */}
        {visibleJumps.length > 0 && (
          <div className="mb-4 text-sm">
            <span className="text-hpi-dark-gray font-semibold">Jump to: </span>
            {visibleJumps.map((j, i) => (
              <span key={j.anchor}>
                {i > 0 && " "}
                <a
                  href={`#${j.anchor}`}
                  className="bg-hpi-pale-blue text-hpi-active-blue hover:bg-hpi-light-blue mr-1 inline-block rounded px-2 py-0.5 text-xs"
                >
                  {j.label}
                </a>
              </span>
            ))}
          </div>
        )}

        {/* Dependents / Description / Details / Attorney / Internal Notes */}
        {s.dependents && (
          <div className="mb-3 text-sm">
            <span className="text-hpi-dark-gray font-semibold">
              Dependents:
            </span>{" "}
            <span className="whitespace-pre-wrap">{s.dependents}</span>
          </div>
        )}
        {s.description && (
          <div className="mb-3 text-sm">
            <span className="text-hpi-dark-gray font-semibold">
              Description:
            </span>{" "}
            <span className="whitespace-pre-wrap">{s.description}</span>
          </div>
        )}
        {s.hobbies && (
          <div className="mb-3 text-sm">
            <span className="text-hpi-dark-gray font-semibold">
              Details/Hobbies:
            </span>{" "}
            <span className="whitespace-pre-wrap">{s.hobbies}</span>
          </div>
        )}
        {[s.attorney, s.law_firm].some(Boolean) && (
          <div className="mb-3 text-sm">
            <span className="text-hpi-dark-gray font-semibold">Attorney:</span>{" "}
            {s.attorney}
            {s.attorney && s.law_firm ? " @ " : ""}
            {s.law_firm}
          </div>
        )}
        {s.internal_notes && (
          <div className="text-hpi-red mb-3 text-sm">
            <span className="font-semibold">Internal Notes:</span>{" "}
            <span className="whitespace-pre-wrap">{s.internal_notes}</span>
          </div>
        )}

        {/* Photos */}
        <Section id="photos" title="Photos">
          {s.photos.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-4">
              {s.photos.map((photo) => (
                <div key={photo.id} className="text-center">
                  {photo.thumb_url ? (
                    <a
                      href={photo.image_url ?? photo.thumb_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={photo.thumb_url}
                        alt={photo.display_name}
                        className="h-[120px] w-[120px] rounded object-cover"
                        style={{ objectPosition: objectPositionStyle(photo) }}
                      />
                    </a>
                  ) : (
                    <div className="bg-hpi-light-gray border-hpi-med-gray text-hpi-med-gray flex h-[120px] w-[120px] items-center justify-center rounded border text-xs">
                      Photo
                    </div>
                  )}
                  {photo.caption && (
                    <div className="text-hpi-med-blue mt-1 max-w-[120px] text-xs">
                      {photo.caption}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <Link
            href={s.photos_url}
            className="text-hpi-active-blue hover:text-hpi-med-blue text-sm font-medium"
          >
            Manage Photos
          </Link>
        </Section>

        {/* Addresses */}
        {s.addresses.length > 0 && (
          <Section id="addresses" title="Addresses">
            <ul className="space-y-3">
              {s.addresses.map((addr) => (
                <li key={addr.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <a
                      href={googleMapsUrl(
                        addr.street,
                        addr.city,
                        addr.state,
                        addr.zip,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-hpi-active-blue whitespace-pre-line"
                    >
                      {addr.full_address}
                    </a>
                    {addr.subdivision && (
                      <div className="text-hpi-med-blue text-xs">
                        Subdivision: {addr.subdivision}
                      </div>
                    )}
                    {addr.notes && (
                      <div className="text-hpi-dark-gray mt-1 text-xs whitespace-pre-wrap">
                        {addr.notes}
                      </div>
                    )}
                  </div>
                  {can_edit_sub_items && (
                    <button
                      onClick={() => openEditModal("address", addr)}
                      className="text-hpi-med-blue hover:text-hpi-active-blue mt-0.5 shrink-0 cursor-pointer"
                    >
                      <SquarePen className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {can_delete_sub_items && (
                    <button
                      onClick={() =>
                        handleDeleteSubItem(
                          `/cases/${c.id}/subjects/${s.id}/addresses/${addr.id}`,
                          "address",
                        )
                      }
                      className="text-hpi-med-gray hover:text-hpi-red mt-0.5 shrink-0 cursor-pointer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Criminal History */}
        {s.criminal_records.length > 0 && (
          <Section id="criminal" title="Criminal History">
            <ul className="space-y-3">
              {s.criminal_records.map((cr) => (
                <li key={cr.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <div>
                      {formatDate(cr.offense_date)}
                      {cr.offense_date ? " - " : ""}
                      {cr.conviction}
                      {cr.county ? ` - ${cr.county} County,` : ""} {cr.state}
                      <VerifiedBadge verified={cr.verified} />
                    </div>
                    {cr.disposition && (
                      <div className="text-xs">
                        Disposition: {cr.disposition}
                      </div>
                    )}
                    {cr.notes && (
                      <div className="text-hpi-dark-gray mt-1 text-xs whitespace-pre-wrap">
                        {cr.notes}
                      </div>
                    )}
                  </div>
                  {can_edit_sub_items && (
                    <button
                      onClick={() => openEditModal("criminal", cr)}
                      className="text-hpi-med-blue hover:text-hpi-active-blue mt-0.5 shrink-0 cursor-pointer"
                    >
                      <SquarePen className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {can_delete_sub_items && (
                    <button
                      onClick={() =>
                        handleDeleteSubItem(
                          `/cases/${c.id}/subjects/${s.id}/criminal_records/${cr.id}`,
                          "criminal record",
                        )
                      }
                      className="text-hpi-med-gray hover:text-hpi-red mt-0.5 shrink-0 cursor-pointer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Education */}
        {s.education_records.length > 0 && (
          <Section id="education" title="Education">
            <ul className="space-y-3">
              {s.education_records.map((er) => (
                <li key={er.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <div>
                      {er.start_date && (
                        <span>{formatMonthYear(er.start_date)}</span>
                      )}
                      {(er.start_date ?? er.end_date) != null && (
                        <span> to </span>
                      )}
                      {er.end_date && (
                        <span>{formatMonthYear(er.end_date)}</span>
                      )}
                      {(er.start_date ?? er.end_date) != null && " - "}
                      {er.institution}
                      {er.city ? ` - ${er.city},` : ""} {er.state}
                      <VerifiedBadge verified={er.verified} />
                    </div>
                    {er.major && (
                      <div className="text-xs">Major: {er.major}</div>
                    )}
                    {er.degree && (
                      <div className="text-xs">Degree: {er.degree}</div>
                    )}
                  </div>
                  {can_edit_sub_items && (
                    <button
                      onClick={() => openEditModal("education", er)}
                      className="text-hpi-med-blue hover:text-hpi-active-blue mt-0.5 shrink-0 cursor-pointer"
                    >
                      <SquarePen className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {can_delete_sub_items && (
                    <button
                      onClick={() =>
                        handleDeleteSubItem(
                          `/cases/${c.id}/subjects/${s.id}/education_records/${er.id}`,
                          "education record",
                        )
                      }
                      className="text-hpi-med-gray hover:text-hpi-red mt-0.5 shrink-0 cursor-pointer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Employment History */}
        {s.employment_records.length > 0 && (
          <Section id="employment" title="Employment History">
            <ul className="space-y-3">
              {s.employment_records.map((emp) => (
                <li key={emp.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <div>
                      {emp.start_date && (
                        <span>{formatMonthYear(emp.start_date)}</span>
                      )}
                      {(emp.start_date ?? emp.end_date) != null && (
                        <span> to </span>
                      )}
                      {emp.end_date && (
                        <span>{formatMonthYear(emp.end_date)}</span>
                      )}
                      {(emp.start_date ?? emp.end_date) != null && " - "}
                      {emp.company_name}
                      <VerifiedBadge verified={emp.verified} />
                    </div>
                    {emp.full_address && (
                      <div className="text-xs">{emp.full_address}</div>
                    )}
                    {emp.position && (
                      <div className="text-xs">Position: {emp.position}</div>
                    )}
                    {emp.supervisor && (
                      <div className="text-xs">
                        Supervisor: {emp.supervisor}
                      </div>
                    )}
                    {emp.schedule && (
                      <div className="text-xs">Schedule: {emp.schedule}</div>
                    )}
                    {emp.reason_for_leaving && (
                      <div className="text-xs">
                        Reason for Leaving: {emp.reason_for_leaving}
                      </div>
                    )}
                  </div>
                  {can_edit_sub_items && (
                    <button
                      onClick={() => openEditModal("employment", emp)}
                      className="text-hpi-med-blue hover:text-hpi-active-blue mt-0.5 shrink-0 cursor-pointer"
                    >
                      <SquarePen className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {can_delete_sub_items && (
                    <button
                      onClick={() =>
                        handleDeleteSubItem(
                          `/cases/${c.id}/subjects/${s.id}/employment_records/${emp.id}`,
                          "employment record",
                        )
                      }
                      className="text-hpi-med-gray hover:text-hpi-red mt-0.5 shrink-0 cursor-pointer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Vehicles */}
        {s.vehicles.length > 0 && (
          <Section id="vehicles" title="Vehicles">
            <ul className="space-y-3">
              {s.vehicles.map((v) => (
                <li key={v.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <div>{v.description}</div>
                    {(v.tag ?? v.tag_state) != null && (
                      <div className="text-xs">
                        Tag: {v.tag_state} #{v.tag}
                      </div>
                    )}
                    {v.notes && (
                      <div className="text-hpi-dark-gray mt-1 text-xs whitespace-pre-wrap">
                        {v.notes}
                      </div>
                    )}
                  </div>
                  {can_edit_sub_items && (
                    <button
                      onClick={() => openEditModal("vehicle", v)}
                      className="text-hpi-med-blue hover:text-hpi-active-blue mt-0.5 shrink-0 cursor-pointer"
                    >
                      <SquarePen className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {can_delete_sub_items && (
                    <button
                      onClick={() =>
                        handleDeleteSubItem(
                          `/cases/${c.id}/subjects/${s.id}/vehicles/${v.id}`,
                          "vehicle",
                        )
                      }
                      className="text-hpi-med-gray hover:text-hpi-red mt-0.5 shrink-0 cursor-pointer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Insurance Claim */}
        {hasInsurance && (
          <Section id="insurance" title="Insurance Claim Information">
            <div className="grid grid-cols-[1fr_250px] gap-4">
              <div className="space-y-1">
                {s.ins_date_of_loss && (
                  <div>
                    <span className="font-semibold">Date of Loss:</span>{" "}
                    {s.ins_date_of_loss}
                  </div>
                )}
                {s.ins_injury && (
                  <div>
                    <span className="font-semibold">Injury:</span>{" "}
                    {s.ins_injury}
                  </div>
                )}
                {s.ins_limits && (
                  <div>
                    <span className="font-semibold">Restrictions:</span>{" "}
                    {s.ins_limits}
                  </div>
                )}
                {s.ins_contact && (
                  <div>
                    <span className="font-semibold">Contact:</span>{" "}
                    {s.ins_contact}
                    {s.ins_contact_phone && (
                      <span> - Phone: {formatPhone(s.ins_contact_phone)}</span>
                    )}
                  </div>
                )}
                {s.ins_rehab_loc && (
                  <div className="mt-2">
                    <div className="font-semibold">Rehab:</div>
                    <div className="whitespace-pre-wrap">{s.ins_rehab_loc}</div>
                  </div>
                )}
                {s.ins_accident && (
                  <div className="mt-2">
                    <div className="font-semibold">Accident:</div>
                    <div className="whitespace-pre-wrap">{s.ins_accident}</div>
                  </div>
                )}
                {s.ins_appt_info && (
                  <div className="mt-2">
                    <div className="font-semibold">Appointments:</div>
                    <div className="whitespace-pre-wrap">{s.ins_appt_info}</div>
                  </div>
                )}
                {s.ins_prev_surveillance_date && (
                  <div className="mt-2">
                    <span className="font-semibold">
                      Previous Surveillance:
                    </span>{" "}
                    {s.ins_prev_surveillance_date}
                  </div>
                )}
              </div>
              <div className="bg-hpi-pale-gray space-y-2 rounded p-3 text-xs">
                {s.ins_rehab && (
                  <div>
                    Going to Rehab:{" "}
                    <span className="font-semibold">{s.ins_rehab}</span>
                  </div>
                )}
                {s.ins_may_we_contact && (
                  <div>
                    HPI may contact employer:{" "}
                    <span className="font-semibold">
                      {s.ins_may_we_contact}
                    </span>
                  </div>
                )}
                {s.ins_prev_surveillance && (
                  <div>
                    Previous Surveillance:{" "}
                    <span className="font-semibold">
                      {s.ins_prev_surveillance}
                    </span>
                  </div>
                )}
                {s.ins_benefits && (
                  <div>
                    Receiving Benefits:{" "}
                    <span className="font-semibold">{s.ins_benefits}</span>
                  </div>
                )}
                {s.ins_working && (
                  <div>
                    Working Light Duty:{" "}
                    <span className="font-semibold">{s.ins_working}</span>
                  </div>
                )}
                {s.ins_appts && (
                  <div>
                    Scheduled Appointments:{" "}
                    <span className="font-semibold">{s.ins_appts}</span>
                  </div>
                )}
              </div>
            </div>
          </Section>
        )}
      </div>

      {/* Sub-item modals */}
      <SubjectAddModals
        activeModal={activeModal}
        onClose={closeModal}
        caseId={c.id}
        subjectId={s.id}
        contactTypes={contact_types}
        editingRecord={editingRecord}
      />
    </AppLayout>
  )
}
